import { useTranslation } from 'react-i18next';
import { Keyboard, Platform, Pressable } from 'react-native';
import { Row } from '../box';
import type { InputProps } from './index';
import { Input } from './index';
import { useUISize } from 'app/utils/web-helpers';
import React from 'react';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconSearch, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

type SearchInputProps = InputProps;

function ClearSearchButton({ onClear, size }: { onClear: () => void; size: number }) {
  return (
    <Pressable hitSlop={10} onPress={onClear} style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}>
      <IconX color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} size={size} />
    </Pressable>
  );
}

function DesktopSearchActions({
  accessibilityLabel,
  onClear,
  onSubmit,
  searchValue,
  submitIcon,
  submitIconVariant,
}: {
  accessibilityLabel: string;
  onClear: () => void;
  onSubmit: () => void;
  searchValue: string;
  submitIcon?: React.ReactNode;
  submitIconVariant: 'filled' | 'plain' | 'none';
}) {
  const renderedSubmitIcon =
    submitIcon ??
    (submitIconVariant === 'none' ? null : (
      <Pressable accessibilityLabel={accessibilityLabel} accessibilityRole="button" hitSlop={8} onPress={onSubmit}>
        <Row
          backgroundColor={submitIconVariant === 'filled' ? 'black' : undefined}
          borderRadius="radius.xl"
          padding={submitIconVariant === 'filled' ? 'spacing.general.x1' : undefined}
        >
          <IconSearch
            color={
              submitIconVariant === 'filled' ? theme.colors['icon.inverse.primary'] : theme.colors['icon.secondary']
            }
            size={submitIconVariant === 'filled' ? 15 : 18}
            strokeWidth={3}
          />
        </Row>
      </Pressable>
    ));

  return (
    <Row alignItems="center" gap="spacing.general.x2">
      {searchValue.trim().length > 0 ? <ClearSearchButton onClear={onClear} size={24} /> : null}
      {renderedSubmitIcon}
    </Row>
  );
}

function SearchInputActions({
  accessibilityLabel,
  isSmallUI,
  onClear,
  onSubmit,
  right,
  searchValue,
  submitIcon,
  submitIconVariant,
}: {
  accessibilityLabel: string;
  isSmallUI: boolean;
  onClear: () => void;
  onSubmit: () => void;
  right?: React.ReactNode;
  searchValue: string;
  submitIcon?: React.ReactNode;
  submitIconVariant: 'filled' | 'plain' | 'none';
}) {
  if (right !== null && right !== undefined) return right;
  if (Platform.OS === 'ios') return null;

  if (Platform.OS === 'android') {
    return searchValue.trim().length > 0 ? <ClearSearchButton onClear={onClear} size={18} /> : null;
  }

  if (isSmallUI) {
    return searchValue.trim().length > 0 ? <ClearSearchButton onClear={onClear} size={20} /> : null;
  }

  return (
    <DesktopSearchActions
      accessibilityLabel={accessibilityLabel}
      onClear={onClear}
      onSubmit={onSubmit}
      searchValue={searchValue}
      submitIcon={submitIcon}
      submitIconVariant={submitIconVariant}
    />
  );
}

export function SearchInput(
  props: SearchInputProps & {
    onSubmit: (searchText: string) => void;
    initialValue?: string;
    searchAsYouType?: boolean;
    submitIconVariant?: 'filled' | 'plain' | 'none';
    submitIcon?: React.ReactNode;
    submitOnEmptyChange?: boolean;
  },
) {
  const { t } = useTranslation('common');
  const { isSmallUI } = useUISize();

  const { onSubmit, placeholder, submitIconVariant = 'filled', submitIcon, onChangeText, ...restProps } = props;
  const [searchState, setSearchState] = React.useState({
    initialValue: props.initialValue,
    value: props.initialValue ?? '',
  });
  let searchValue = searchState.value;
  if (searchState.initialValue !== props.initialValue) {
    searchValue = props.initialValue ?? '';
    setSearchState({ initialValue: props.initialValue, value: searchValue });
  }
  const setSearchValue = (value: string) => setSearchState({ initialValue: props.initialValue, value });
  const shouldSubmitOnEmptyChange = props.submitOnEmptyChange ?? true;

  const onClear = () => {
    setSearchValue('');
    onChangeText?.('');
    if (shouldSubmitOnEmptyChange) {
      onSubmit('');
    }
  };

  return (
    <Input
      {...restProps}
      clearButtonMode="always"
      enablesReturnKeyAutomatically
      left={props.left ?? (isSmallUI ? <IconSearch strokeWidth={ICON_STROKE_WIDTH} /> : undefined)}
      onBlur={Keyboard.dismiss}
      onChangeText={(text) => {
        const trimmed = text.trim();
        setSearchValue(text);
        props.onChangeText?.(text);
        if ((trimmed.length > 0 && props.searchAsYouType) || (trimmed.length === 0 && shouldSubmitOnEmptyChange)) {
          onSubmit(trimmed);
        }
      }}
      onSubmitEditing={() => onSubmit(searchValue.trim())}
      placeholder={placeholder ?? t('search')}
      returnKeyType="search"
      right={
        <SearchInputActions
          accessibilityLabel={t('search')}
          isSmallUI={isSmallUI}
          onClear={onClear}
          onSubmit={() => onSubmit(searchValue.trim())}
          right={props.right}
          searchValue={searchValue}
          submitIcon={submitIcon}
          submitIconVariant={submitIconVariant}
        />
      }
      submitBehavior="blurAndSubmit"
      value={searchValue}
    />
  );
}
